import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useChannelActionContext,
  useChannelStateContext,
  useChatContext,
} from 'stream-chat-react';
import { LocalMessage } from 'stream-chat';
import PinnedMessageBanner from './PinnedMessageBanner';
import {
  canUnpinPinnedMessage,
  getLatestPinnedMessage,
  getPinnedMessagePreviewText,
  getStalePinnedMessages,
} from '../../../utils/pinnedMessage';
import { useConfirm } from '../../../hooks/useConfirm';
import { ButtonAlignement } from '../../../components/ConfirmModal';
import { ButtonVariant } from '../../../config/colors';

const PINNED_MESSAGE_EVENTS = [
  'message.updated',
  'message.deleted',
  'channel.updated',
] as const;

const PinnedMessageBannerContainer: React.FC = () => {
  const { channel, channelCapabilities } = useChannelStateContext(
    'PinnedMessageBannerContainer',
  );
  const { jumpToMessage } = useChannelActionContext(
    'PinnedMessageBannerContainer',
  );
  const { client } = useChatContext('PinnedMessageBannerContainer');
  const { openConfirm } = useConfirm();
  const isCleaningStalePinsRef = useRef(false);
  const [latestPinnedMessage, setLatestPinnedMessage] = useState<LocalMessage | null>(
    () => getLatestPinnedMessage(channel.state.pinnedMessages),
  );

  const refreshLatestPinnedMessage = useCallback(() => {
    setLatestPinnedMessage(getLatestPinnedMessage(channel.state.pinnedMessages));
  }, [channel]);

  const canUnpin = useMemo(
    () => canUnpinPinnedMessage(channelCapabilities),
    [channelCapabilities],
  );

  const unpinMessageById = useCallback(
    async (message: LocalMessage) => {
      try {
        await client.unpinMessage(message);
      } catch (error) {
        console.error('Failed to unpin message', error);
        throw error;
      }
    },
    [client],
  );

  const cleanupStalePinnedMessages = useCallback(async () => {
    if (!canUnpin || isCleaningStalePinsRef.current) {
      return;
    }

    const staleMessages = getStalePinnedMessages(channel.state.pinnedMessages);
    if (staleMessages.length === 0) {
      return;
    }

    isCleaningStalePinsRef.current = true;

    try {
      for (const message of staleMessages) {
        await unpinMessageById(message);
      }
    } finally {
      isCleaningStalePinsRef.current = false;
      refreshLatestPinnedMessage();
    }
  }, [canUnpin, channel, refreshLatestPinnedMessage, unpinMessageById]);

  useEffect(() => {
    refreshLatestPinnedMessage();

    const handleChannelEvent = () => {
      refreshLatestPinnedMessage();
      void cleanupStalePinnedMessages();
    };

    PINNED_MESSAGE_EVENTS.forEach((eventName) => {
      channel.on(eventName, handleChannelEvent);
    });

    void cleanupStalePinnedMessages();

    return () => {
      PINNED_MESSAGE_EVENTS.forEach((eventName) => {
        channel.off(eventName, handleChannelEvent);
      });
    };
  }, [channel, cleanupStalePinnedMessages, refreshLatestPinnedMessage]);

  const previewText = useMemo(() => {
    if (!latestPinnedMessage) {
      return '';
    }

    return getPinnedMessagePreviewText(latestPinnedMessage);
  }, [latestPinnedMessage]);

  const handleSelectPinnedMessage = useCallback(() => {
    if (!latestPinnedMessage?.id) {
      return;
    }

    jumpToMessage(latestPinnedMessage.id);
  }, [jumpToMessage, latestPinnedMessage]);

  const unpinMessage = useCallback(async () => {
    if (!latestPinnedMessage) {
      return;
    }

    await unpinMessageById(latestPinnedMessage);
  }, [latestPinnedMessage, unpinMessageById]);

  const handleRequestUnpin = useCallback(() => {
    if (!latestPinnedMessage || !canUnpin) {
      return;
    }

    openConfirm({
      title: 'Unpin message?',
      message: 'This message will no longer be pinned for everyone in this chat.',
      buttons: [
        {
          label: 'Cancel',
          onClick: () => {},
          variant: ButtonVariant.transparent,
        },
        {
          label: 'Unpin',
          onClick: () => {
            void unpinMessage();
          },
          variant: ButtonVariant.danger,
        },
      ],
      buttonAlignement: ButtonAlignement.row,
    });
  }, [canUnpin, latestPinnedMessage, openConfirm, unpinMessage]);

  if (!latestPinnedMessage) {
    return null;
  }

  return (
    <div className="absolute top-2 left-3 right-3 z-10 pointer-events-none">
      <PinnedMessageBanner
        pinnedMessage={latestPinnedMessage}
        previewText={previewText}
        canUnpin={canUnpin}
        onSelectPinnedMessage={handleSelectPinnedMessage}
        onRequestUnpin={handleRequestUnpin}
      />
    </div>
  );
};

export default PinnedMessageBannerContainer;
